<template>
  <div class="flex flex-col w-full">
    <!-- Layout responsivo: una columna por defecto, dos columnas en md -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <!-- Columna izquierda -->
      <div class="flex flex-col space-y-4">

        <expandable-panel :item="_GetOptionsConfig('config_layer_specifications')">
          <div class="text-sm text-gray-700">
            <div class="flex flex-col space-y-3">
              <b-field :label="$t('name')">
                <b-input v-model="form.name" placeholder="Nombre de la capa"></b-input>
              </b-field>
              <b-field :label="$t('icon')">
                <b-input v-model="form.icon" placeholder="Icono de la capa" disabled></b-input>
              </b-field>
              <b-field :label="$t('weight')">
                <b-input v-model="form.weight" placeholder="Peso de la capa" disabled></b-input>
              </b-field>
              <b-field :label="$t('order')">
                <b-input v-model="form.order_column" placeholder="Orden de la capa" disabled></b-input>
              </b-field>
            </div>
          </div>
        </expandable-panel>

        <expandable-panel :item="_GetOptionsConfig('config_execution_schedule')">
          <div class="text-sm text-gray-700">
            <div class="flex flex-col space-y-3">
              <l-form-double-date
                  :form="form"
                  :startField="'scheduled_start_date'"
                  :endField="'scheduled_end_date'"
                  :allowNull="true"
                  position="is-top-right"
                  :appendToBody="true"
                  label="scheduled_date"  />
              <div class="flex items-center space-x-2 mt-1">
                <b-button
                  size="is-small"
                  type="is-light"
                  icon-pack="fas"
                  icon-left="trash-alt"
                  :disabled="form.scheduled_start_date == null"
                  @click="form.scheduled_start_date = null"
                >{{ $t('clear_start_date') }}</b-button>
                <b-button
                  size="is-small"
                  type="is-light"
                  icon-pack="fas"
                  icon-left="trash-alt"
                  :disabled="form.scheduled_end_date == null"
                  @click="form.scheduled_end_date = null"
                >{{ $t('clear_end_date') }}</b-button>
                <b-button
                  size="is-small"
                  type="is-light"
                  icon-pack="fas"
                  icon-left="trash-alt"
                  :disabled="form.scheduled_start_date == null && form.scheduled_end_date == null"
                  @click="form.scheduled_start_date = null; form.scheduled_end_date = null"
                >{{ $t('clear_dates') }}</b-button>
              </div>
            </div>
          </div>
        </expandable-panel>

      </div>

      <!-- Columna derecha -->
      <div class="flex flex-col space-y-4">
      </div>
    </div>
  </div>
</template>

<script>
import Form from 'vform';

import ExpandablePanel from '../layer-statuses/expandable-panel.vue';
import { editPartialLayer } from '../../services/layers.service.js';

export default {
  name: 'LayerConfig',

  components: {
    ExpandablePanel,
  },

  props: {
    layer: {
      type: Object,
      required: true  // o default: null
    }
  },

  data() {
    return {
      item: null,
      optionsConfig: null,
      isInitializing: true,

      saveConfigurationTimeout: null, // Timeout para debounce del guardado
      originalFormData: null, // Valores originales del formulario para comparar cambios

      form: new Form({
        name: null,
        icon: null,
        weight: 0,
        order_column: 0,
        scheduled_start_date: null,
        scheduled_end_date: null,
      }),
    };
  },

  created() {
    this.optionsConfig = this._getConfigOptions();
    this._initializeForm();
    // Marcar que la inicialización ha terminado y guardar valores originales
    this.$nextTick(() => {
      this.isInitializing = false;
      this.originalFormData = { ...this.form.data() };
    });
  },

  beforeDestroy() {
    // Limpiar el timeout al destruir el componente para evitar memory leaks
    if (this.saveConfigurationTimeout) {
      clearTimeout(this.saveConfigurationTimeout);
      this.saveConfigurationTimeout = null;
    }
  },

  computed: {
    formData() {
      // Computed property que devuelve los datos del formulario
      // Esto permite observar todos los cambios de una vez
      return this.form.data();
    }
  },

  watch: {
    'layer.config': {
      handler() {
        this.isInitializing = true;
        this._initializeForm();
        this.$nextTick(() => {
          this.isInitializing = false;
        });
      },
      deep: true
    },
    formData: {
      handler() {
        if (!this.isInitializing) {
          this._debouncedSaveConfiguration();
        }
      },
      deep: true
    }
  },

  methods: {
    _getConfigOptions() {
      return [
        {
          code: 'config_layer_specifications',
          name: 'Especificaciones de la capa',
          description: 'Gestión de identidad, simbología y jerarquía de visualización.',
          icon: 'cog',
          order: 0,
          type: 'item',
        },
        {
          code: 'config_execution_schedule',
          name: 'Planificación del Cronograma de Ejecución',
          description: "Establece el horizonte temporal de la capa mediante la configuración de fechas de inicio y finalización para el despliegue de los componentes en planta.",
          icon: 'calendar-alt',
          order: 1,
          type: 'item',
        }
      ]
    },

    _GetOptionsConfig(code) {
      return this.optionsConfig.find(option => option.code === code);
    },

    _initializeForm() {
      this.form.code = this.layer.code;
      this.form.name = this.layer.name;
      this.form.icon = this.layer.icon;
      this.form.weight = this.layer.weight;
      this.form.order_column = this.layer.order_column;

      if (this.layer.scheduled_start_date !== undefined) {
        this.form.scheduled_start_date = this.layer.scheduled_start_date
          ? new Date(this.layer.scheduled_start_date+'T00:00:00') : null // '2021-06-01T00:00:00' es para evitar se reste uno por la sona horario que automativamente lo hace el navegador
      }

      if (this.layer.scheduled_end_date !== undefined) {
        this.form.scheduled_end_date = this.layer.scheduled_end_date
          ? new Date(this.layer.scheduled_end_date + 'T00:00:00') : null;
      }
    },

    /**
     * Debounce para el guardado de configuración
     * Espera 800ms después del último cambio antes de ejecutar el guardado
     * Esto permite que el usuario configure múltiples opciones sin disparar múltiples guardados
     */
     _debouncedSaveConfiguration() {
      // Limpiar el timeout anterior si existe
      if (this.saveConfigurationTimeout) {
        clearTimeout(this.saveConfigurationTimeout);
      }

      // Establecer nuevo timeout (800ms es un tiempo estándar para formularios de configuración)
      this.saveConfigurationTimeout = setTimeout(() => {
        this._saveConfiguration();
        this.saveConfigurationTimeout = null;
      }, 800);
    },

    /**
     * Compara los valores actuales del formulario con los originales
     * y retorna solo los campos que han cambiado
     * @param {Object} currentData - Datos actuales del formulario
     * @param {Object} originalData - Datos originales del formulario
     * @returns {Object} Objeto con solo los campos que cambiaron
     */
    _getChangedFields(currentData, originalData) {
      if (!originalData) return currentData;

      const changedFields = {};

      Object.keys(currentData).forEach(key => {
        const currentValue = currentData[key];
        const originalValue = originalData[key];

        // Comparar valores (considerando null, undefined, y tipos)
        if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
          changedFields[key] = currentValue;
        }
      });

      return changedFields;
    },

    /**
     * Convierte un valor de fecha (Date de JS, string ISO o null) a 'YYYY-MM-DD'
     * en hora LOCAL. Devuelve null si el valor está vacío o no es una fecha válida.
     * Se usa para no enviar objetos Date crudos (que se serializan a ISO con hora/Z)
     * a columnas DATE del backend.
     */
    _ToYmdLocal(value) {
      if (!value) return null;
      const d = (value instanceof Date) ? value : new Date(value);
      if (isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },

    async _saveConfiguration() {
      // console.log('=== Formulario - Claves y Valores ===');

      // Obtener los datos del formulario
      const formData = this.form.data();

      // Obtener solo los campos que han cambiado
      const changedFields = this._getChangedFields(formData, this.originalFormData);

      // Si no hay cambios, no enviar nada
      if (Object.keys(changedFields).length === 0) {
        return;
      }

      // Listar todas las claves y valores
      // Object.keys(formData).forEach(key => {
      //   console.log(`${key}:`, formData[key]);
      // });

      // console.log('changedFields: ', changedFields);

      // Las columnas scheduled_*_date son DATE (Y-m-d). El form las guarda como
      // objetos Date de JS; enviarlas tal cual las serializa a ISO
      // ("2026-09-30T00:00:00.000Z") y MySQL (columna DATE, modo estricto) rechaza
      // el guardado (1292 Incorrect date value). Formateamos a Y-m-d en hora LOCAL
      // (el Date se creó a medianoche local) para no correr el día por zona horaria.
      ['scheduled_start_date', 'scheduled_end_date'].forEach((key) => {
        if (key in changedFields) {
          changedFields[key] = this._ToYmdLocal(changedFields[key]);
        }
      });

      // Construir payload solo con los campos que cambiaron
      const payload = {
        data: {
          ...changedFields,
        },
      };

      try {
        const response = await editPartialLayer(this.layer.id, payload);

        if( response.success ) {
          // Actualizar los valores originales después de guardar exitosamente
          this.originalFormData = { ...this.form.data() };

          this.$toast.open({
            message: this.$t("assignment_update_success"),
            type: "is-success",
            position: "is-top-right",
          });

          // Refresh parcial: nombre/fechas de la capa no afectan al progreso, así que
          // el panel parchea in-place sin re-fetch ni reset de navegación.
          this.$emit('epc-pictograms-reload', {
            scope: 'layer',
            layerId: this.layer.id,
            changes: changedFields,
            affectsProgress: false,
          })

        } else {
          this.$toast.open({
            message: response.error,
            type: "is-danger",
            position: "is-top-right",
          });
        }
      } catch (error) {
        console.error("Error al guardar datos de las capas del pictograma:", error);
        this.$toast.open({
          message: error.error_msg || this.$t("assignment_update_error"),
          type: "is-danger",
          position: "is-top-right",
        });
      } finally {

      }
    }
  }
}
</script>
